'use client'

import Link from 'next/link'
import { useTheme } from '@/contexts/ThemeContext'
import { ArrowLeft, CheckCircle, Lightbulb } from 'lucide-react'

export default function FinsPage() {
  const { theme } = useTheme()
  const isDark = theme === 'dark'

  return (
    <div className={`min-h-screen py-4 xxs:py-3 px-3 xxs:px-2 ${
      isDark ? 'bg-gray-900' : 'bg-gradient-to-b from-green-50 to-white'
    }`}>
      <div className="max-w-3xl mx-auto">
        <Link
          href="/equipment"
          className={`inline-flex items-center gap-1 mb-4 xxs:mb-3 text-sm xxs:text-xs ${
            isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'
          }`}
        >
          <ArrowLeft className="w-4 h-4 xxs:w-3.5 xxs:h-3.5" />
          장비 가이드 목록
        </Link>

        <article className={`rounded-xl shadow-lg overflow-hidden ${
          isDark ? 'bg-gray-800' : 'bg-white'
        }`}>
          {/* 헤더 */}
          <div className={`p-6 xxs:p-4 ${isDark ? 'bg-green-900/30' : 'bg-green-50'}`}>
            <h1 className={`text-2xl xxs:text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              프리다이빙 핀 선택 가이드
            </h1>
            <p className={`mt-2 text-sm xxs:text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              프리다이빙에서 핀은 추진력을 만드는 가장 중요한 장비입니다. 자신에게 맞는 핀을 선택하세요.
            </p>
          </div>

          <div className="p-6 xxs:p-4 space-y-6 xxs:space-y-4">
            {/* 핀의 종류 */}
            <div>
              <h2 className={`text-xl xxs:text-lg font-bold mb-4 xxs:mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                핀의 종류
              </h2>
              <div className="space-y-3 xxs:space-y-2">
                {[
                  {
                    title: '1. 플라스틱 핀 (Plastic Fins)',
                    pros: ['저렴한 가격 (10-20만원)', '내구성 좋음', '초보자에게 적합', '관리가 쉬움'],
                    cons: ['효율성 낮음', '무거움', '반응성 떨어짐']
                  },
                  {
                    title: '2. 유리섬유 핀 (Fiberglass Fins)',
                    pros: ['적절한 강성과 유연성', '좋은 가성비', '중급자에게 최적', '다양한 강도 선택 가능'],
                    cons: ['관리 주의 필요', '가격 (30-50만원)', '파손 위험']
                  },
                  {
                    title: '3. 카본 핀 (Carbon Fins)',
                    pros: ['최고의 효율성', '가볍고 반응성 좋음', '에너지 절약', '고급자용'],
                    cons: ['고가 (60-150만원)', '섬세한 관리 필요', '파손 시 수리 어려움']
                  },
                ].map((fin, idx) => (
                  <div key={idx} className={`p-4 xxs:p-3 rounded-xl ${isDark ? 'bg-blue-900/20' : 'bg-blue-50'}`}>
                    <h3 className={`font-bold text-base xxs:text-sm mb-3 xxs:mb-2 ${isDark ? 'text-blue-300' : 'text-blue-800'}`}>
                      {fin.title}
                    </h3>
                    <div className="grid grid-cols-2 gap-4 xxs:gap-2">
                      <div>
                        <p className={`font-semibold text-sm xxs:text-xs mb-1 ${isDark ? 'text-green-400' : 'text-green-600'}`}>장점</p>
                        <ul className={`space-y-1 text-xs xxs:text-[10px] ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                          {fin.pros.map((item, i) => (
                            <li key={i}>• {item}</li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <p className={`font-semibold text-sm xxs:text-xs mb-1 ${isDark ? 'text-red-400' : 'text-red-600'}`}>단점</p>
                        <ul className={`space-y-1 text-xs xxs:text-[10px] ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                          {fin.cons.map((item, i) => (
                            <li key={i}>• {item}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 핀 선택 기준 */}
            <div>
              <h2 className={`text-xl xxs:text-lg font-bold mb-4 xxs:mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                핀 선택 기준
              </h2>
              <div className="space-y-2 xxs:space-y-1.5">
                {[
                  {
                    title: '1. 실력 레벨',
                    items: ['초보자: 플라스틱 핀 (soft)', '중급자: 유리섬유 핀 (medium)', '고급자: 카본 핀 (medium-hard)']
                  },
                  {
                    title: '2. 발 크기',
                    items: ['풋 포켓이 발에 딱 맞아야 함', '너무 크면 물렁거림', '너무 작으면 발 통증', '네오프렌 양말 착용 고려']
                  },
                  {
                    title: '3. 체격과 근력',
                    items: ['체격이 작거나 근력 약하면: Soft', '평균 체격: Medium', '체격 크거나 근력 강하면: Hard', '잘못된 강도는 부상 위험']
                  },
                ].map((section, idx) => (
                  <div key={idx} className={`p-4 xxs:p-3 rounded-lg ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                    <h3 className={`font-bold text-sm xxs:text-xs mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {section.title}
                    </h3>
                    <ul className={`space-y-1 text-xs xxs:text-[10px] ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      {section.items.map((item, i) => (
                        <li key={i}>• {item}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            {/* 구매 팁 */}
            <div className={`p-4 xxs:p-3 rounded-xl ${isDark ? 'bg-yellow-900/20' : 'bg-yellow-50'}`}>
              <div className="flex items-start gap-2">
                <Lightbulb className={`w-5 h-5 xxs:w-4 xxs:h-4 flex-shrink-0 ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`} />
                <div>
                  <h3 className={`font-bold text-base xxs:text-sm mb-2 ${isDark ? 'text-yellow-300' : 'text-yellow-800'}`}>
                    핀 구매 팁
                  </h3>
                  <ul className={`space-y-1 text-sm xxs:text-xs ${isDark ? 'text-yellow-200/80' : 'text-yellow-700'}`}>
                    <li>• 처음에는 저렴한 핀으로 시작하세요</li>
                    <li>• 풋 포켓과 블레이드를 따로 살 수 있습니다</li>
                    <li>• 중고 거래도 좋은 선택입니다</li>
                    <li>• 반드시 착용해보고 구매하세요</li>
                    <li>• 세트 구매 시 할인 혜택 있음</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* 핀 관리 방법 */}
            <div className={`p-4 xxs:p-3 rounded-xl ${isDark ? 'bg-blue-900/20' : 'bg-blue-50'}`}>
              <h3 className={`font-bold text-base xxs:text-sm mb-3 xxs:mb-2 ${isDark ? 'text-blue-300' : 'text-blue-800'}`}>
                핀 관리 방법
              </h3>
              <div className="space-y-1.5 xxs:space-y-1">
                {[
                  '사용 후 민물로 깨끗이 헹구기',
                  '그늘에서 완전히 건조',
                  '직사광선 피하기 (변형 방지)',
                  '무거운 물건 올려놓지 않기',
                  '전용 핀 백에 보관',
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <CheckCircle className={`w-4 h-4 xxs:w-3.5 xxs:h-3.5 ${isDark ? 'text-blue-400' : 'text-blue-500'}`} />
                    <span className={`text-sm xxs:text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </article>
      </div>
    </div>
  )
}
